import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { Message, Button, Confirm } from 'semantic-ui-react';
import { toNumber } from 'lodash';
import { connect } from 'react-redux';

import LoadingDataEnhancer from '../../containers/LoadingData';
import withPagination from '../../containers/WithPagination';
import ExceptionList from './List';

import DealerExceptionForm from './DealerException/Form';

import autoCancellationExceptionListQuery from '../../graphql/autoCancellationExceptionListQuery';
import createAutoCancellationExceptionMutation from '../../graphql/createAutoCancellationExceptionMutation';
import updateAutoCancellationExceptionMutation from '../../graphql/updateAutoCancellationExceptionMutation';
import deleteAutoCancellationExceptionMutation from '../../graphql/deleteAutoCancellationExceptionMutation';
import { setPage } from '../../data/page/actions';

const defaultDealerException = {
  id: 0,
  dealerNumber: '',
  dealerName: '',
  gracePeriod: '',
  exceptionEndTime: '',
  createdBy: '',
  createdAt: '',
  lastUpdatedBy: '',
  lastUpdatedAt: '',
};
const fieldsToCompare = ['gracePeriod', 'exceptionEndTime'];

class DealerExceptions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDealerException: null,
      isDeleteConfirmationOpen: false,
      isModalOpen: false,
      formError: null,
      formSubmitting: null,
      flashMessage: null,
      isNegativeFlashMessage: false,
    };
  }

  static isChange(values, selectedDealerException, fields) {
    return fields.some(
      field => values[field] !== selectedDealerException[field]
    );
  }

  handleDeleteCancel = () => {
    this.setState({ isDeleteConfirmationOpen: false });
  };

  onSelectDealerException = (action, dealerException) => {
    this.setState({
      selectedDealerException: dealerException,
      flashMessage: null,
      isNegativeFlashMessage: false,
    });

    switch (action) {
      case 'Edit':
        this.setState({
          isModalOpen: true,
        });
        break;
      case 'Delete':
        this.setState({
          isDeleteConfirmationOpen: true,
        });
        break;
      default:
        this.setState({
          selectedDealerException: null,
        });
    }
  };

  onFormSubmitHandler = async values => {
    const {
      createAutoCancellationExceptionMutation: createMutation,
      updateAutoCancellationExceptionMutation: updateMutation,
    } = this.props;
    const { selectedDealerException } = this.state;
    this.setState({
      formSubmitting: true,
    });
    const { action } = values;
    try {
      switch (action) {
        case 'Create':
          await this.onExcuteMutationDealerException(
            createMutation,
            'createAutoCancellationException',
            values,
            action
          );
          break;
        case 'Update':
          if (DealerExceptions.isChange(values, selectedDealerException, fieldsToCompare)) {
            await this.onExcuteMutationDealerException(
              updateMutation,
              'updateAutoCancellationException',
              values,
              action
            );
          } else {
            // unchanged
            this.onFormCloseHandler();
          }
          break;
        default:
          break;
      }
    } catch (error) {
      this.onFailedSubmitDealerException(
        action,
        Array.isArray(error) ? error : error.message
      );
    }
  };

  handleDeleteConfirm = async() => {
    const {
      selectedDealerException: { id },
    } = this.state;
    const { deleteAutoCancellationExceptionMutation: deleteMutation } = this.props;
    const action = 'Delete';
    try {
      await this.onExcuteMutationDealerException(
        deleteMutation,
        'deleteAutoCancellationException',
        { id },
        action
      );
    } catch (error) {
      this.onFailedSubmitDealerException(
        action,
        Array.isArray(error) ? error : error.message
      );
    }
  };

  onFormCloseHandler = () => {
    this.setState({
      isModalOpen: false,
      formError: null,
      formSubmitting: null,
    });
  };

  onClickNewDealerException = () => {
    this.setState({
      selectedDealerException: defaultDealerException,
      isModalOpen: true,
    });
  };

  successSubmitDealerException = action => {
    this.setState({
      formSubmitting: false,
      formError: null,
      flashMessage: `Successfully ${action}d Dealer Exception.`,
    });
  };

  onFailedSubmitDealerException = (action, errorMessage) => {
    if (action === 'Delete') {
      this.setState({
        isDeleteConfirmationOpen: false,
        flashMessage: `Unsuccessfully Deleted Dealer Exception with error: ${errorMessage}`,
        isNegativeFlashMessage: true,
      });
    } else {
      this.setState({
        formSubmitting: false,
        formError: errorMessage,
      });
    }
  };

  static getCreateExpiredVraGracePeriodMutationInput(action, values) {
    return {
      exceptionType: 'DEALER',
      exceptionSourceId: toNumber(values.dealerNumber),
      gracePeriod: toNumber(values.gracePeriod),
      exceptionEndTime: values.exceptionEndTime,
      id: values.id ? toNumber(values.id) : 0,
    };
  }

  onExcuteMutationDealerException = async(
    mutation,
    mutationResultName,
    values,
    action
  ) => {
    const { setPage: setPageAction, data } = this.props;
    const input = DealerExceptions.getCreateExpiredVraGracePeriodMutationInput(
      action,
      values
    );
    await mutation({
      variables: { ...input },
    })
      .then(({ data: mutationData }) => {
        const { success, msg } = mutationData[mutationResultName];
        if (success) {
          this.successSubmitDealerException(action);
          this.onFormCloseHandler();
          if (action === 'Delete') {
            this.setState({
              isDeleteConfirmationOpen: false,
            });
          }
          setPageAction({});
          data.refetch();
        } else {
          this.onFailedSubmitDealerException(action, msg);
        }
      })
      .catch(error => {
        this.onFailedSubmitDealerException(
          action,
          Array.isArray(error) ? error : error.message
        );
      });
  };

  componentDidMount() {
    const { setPage: setPageAction } = this.props;
    setPageAction({});
  }

  render() {
    const {
      data: { autoCancellationExceptionList },
    } = this.props;

    const {
      selectedDealerException,
      isDeleteConfirmationOpen,
      formSubmitting,
      isModalOpen,
      formError,
      flashMessage,
      isNegativeFlashMessage,
    } = this.state;
    return (
      <div>
        {flashMessage && (
          <Message
            onDismiss={this.handleMessageDismiss}
            content={flashMessage}
            positive
            negative={isNegativeFlashMessage}
          />
        )}
        <Button
          inverted
          color='green'
          onClick={this.onClickNewDealerException}
        >
          New Dealer Exception
        </Button>
        <br />
        <br />
        <ExceptionList
          onSelectException={this.onSelectDealerException}
          exceptions={autoCancellationExceptionList}
          exceptionType='DEALER'
        />

        {isModalOpen && (
          <DealerExceptionForm
            isModalOpen={isModalOpen}
            onCloseHandler={this.onFormCloseHandler}
            onSubmitHandler={this.onFormSubmitHandler}
            dealerException={selectedDealerException}
            error={formError}
            submitting={formSubmitting}
          />
        )}

        <Confirm
          open={isDeleteConfirmationOpen}
          onCancel={this.handleDeleteCancel}
          onConfirm={this.handleDeleteConfirm}
        />
      </div>
    );
  }
}

DealerExceptions.propTypes = {
  createAutoCancellationExceptionMutation: PropTypes.func.isRequired,
  updateAutoCancellationExceptionMutation: PropTypes.func.isRequired,
  deleteAutoCancellationExceptionMutation: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  data: PropTypes.shape({
    autoCancellationExceptionList: PropTypes.arrayOf(PropTypes.object),
    refetch: PropTypes.func,
    user: PropTypes.object,
  }).isRequired,
};

const mapDispatchToProps = dispatch => ({
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { user },
  } = state;
  return {
    user,
  };
};
export default compose(
  withRouter,
  withPagination(
    autoCancellationExceptionListQuery,
    'autoCancellationExceptionList',
    { exceptionTypes: 'DEALER' }
  ),
  graphql(createAutoCancellationExceptionMutation, {
    name: 'createAutoCancellationExceptionMutation',
  }),
  graphql(updateAutoCancellationExceptionMutation, {
    name: 'updateAutoCancellationExceptionMutation',
  }),
  graphql(deleteAutoCancellationExceptionMutation, {
    name: 'deleteAutoCancellationExceptionMutation',
  }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(DealerExceptions);
